/* eslint-disable prettier/prettier */
import classnames from 'classnames';
import { Card, CardContent, CardHeader, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useChatState, useChat } from 'hooks';
import { CrossIcon } from 'assets';
import { MessagesList } from './MessagesList';
import { ChatInput } from './ChatInput';

import { ApiUser, ApiWaitingRoomLinkProfile } from 'types';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { useMemo } from 'react';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import styles from './Chat.module.scss';

type ChatProperties = {
  className?: string;
  shouldIgnoreReceiveMessages?: boolean;
  forceLightTheme?: boolean;
};

export function Chat({ className, shouldIgnoreReceiveMessages = true, forceLightTheme }: ChatProperties): JSX.Element {
  const { t } = useTranslation('providerRoom');
  const { chatName } = useChatState();
  const { conversation, closeChat, isChatOpen } = useChat();
  const user: ApiUser | null = useAppSelector(selectUser);
  const waitingRoomProfile: ApiWaitingRoomLinkProfile | null = useAppSelector(selectWaitingRoomProfile);

  const { preferedDisplayName } = waitingRoomProfile || {};

  const preferredChatName = useMemo(() => {
    return preferedDisplayName && preferedDisplayName !== '' ? preferedDisplayName : chatName;
  }, [chatName, preferedDisplayName]);

  const shouldDisplayChat: boolean = useMemo(() => {
    const canReceiveMessages = !user || user?.clinicUser.receiveMessages || !shouldIgnoreReceiveMessages;

    return isChatOpen && canReceiveMessages;
  }, [isChatOpen, user, shouldIgnoreReceiveMessages]);

  const handleTyping = (): void => {
    if (conversation) {
      conversation.typing();
    }
  };

  const { sendMessage } = useChat();

  const handleSendMessage = (message: string | FormData): Promise<number> => {
    return sendMessage(message as string);
  };

  return (
    <>
      {shouldDisplayChat && (
        <Card className={classnames(styles.chat, className, { [styles.forceLight]: forceLightTheme })}>
          <CardHeader
            title={preferredChatName}
            action={
              <IconButton aria-label={t('aria.close')} onClick={closeChat}>
                <CrossIcon className={styles.close} color='primary' />
              </IconButton>
            }
          />
          <CardContent>
            <MessagesList isGroup={false} forceLightTheme={forceLightTheme} />
            <ChatInput isGroup={false} onTyping={handleTyping} sendMessage={handleSendMessage} />
          </CardContent>
        </Card>
      )}
    </>
  );
}
